import { useState, useEffect } from "react";
import { Bell, Check, X } from "lucide-react";
import axios from "axios";
import BACKEND_URL from "../lib/apiConfig";
import { stripBulletinFormatting } from "./BulletinMessage";

export { BulletinPopup } from "./bulletins/BulletinPopup";
const API = `${BACKEND_URL}/api`;

export function BulletinBell({ userId }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/bulletins/unread/${userId}`)
      .then(res => setUnreadCount((res.data || []).length)).catch(() => {});
    const onAcknowledged = event => {
      if (event.detail?.userId === userId) setUnreadCount(n => Math.max(0, n - 1));
    };
    window.addEventListener("bulletin-acknowledged", onAcknowledged);
    return () => window.removeEventListener("bulletin-acknowledged", onAcknowledged);
  }, [userId]);
  const loadHistory = async () => {
    try { const res = await axios.get(`${API}/bulletins/history/${userId}`); setHistory(res.data || []); }
    catch { /* History may be temporarily unavailable. */ }
  };
  return <div className="relative">
    <button onClick={() => { setShowHistory(p => !p); if (!showHistory) loadHistory(); }}
      className="relative p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.08] transition-colors"
      aria-label="Bulletin history" aria-expanded={showHistory} data-testid="bulletin-bell">
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#FF3B30] rounded-full flex items-center justify-center text-[9px] font-bold text-white"
        data-testid="bulletin-unread-count">{unreadCount}</span>}
    </button>
    {showHistory && <>
      <button aria-label="Close bulletin history" data-testid="bulletin-history-backdrop" className="fixed inset-0 z-40" onClick={() => setShowHistory(false)} />
      <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-24px)] max-h-96 overflow-y-auto bg-[#1A1A1E] border border-white/10 rounded-xl shadow-2xl z-50"
        data-testid="bulletin-history-dropdown">
        <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between">
          <p className="text-xs font-semibold text-zinc-400 uppercase">Bulletin History</p>
          <button aria-label="Close bulletin history" data-testid="bulletin-history-close" onClick={() => setShowHistory(false)} className="text-zinc-600 hover:text-white"><X className="h-3.5 w-3.5" /></button>
        </div>
        {history.length === 0 ? <p className="text-sm text-zinc-600 text-center py-6" data-testid="bulletin-history-empty">No bulletins</p>
          : history.map(b => <div key={b.id} className={`px-3 py-2.5 border-b border-white/[0.03] ${b.acknowledged ? "" : "bg-[#007AFF]/5"}`} data-testid={`bulletin-history-${b.id}`}>
            <div className="flex items-center gap-2">
              {b.acknowledged ? <Check className="h-3 w-3 text-[#39FF14] shrink-0" /> : <div className="w-2 h-2 rounded-full bg-[#007AFF] shrink-0" />}
              <p className="text-xs font-semibold text-white truncate">{b.title}</p>
            </div>
            <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{stripBulletinFormatting(b.message)}</p>
            <p className="text-[9px] text-zinc-700 mt-1">{new Date(b.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
          </div>)}
      </div>
    </>}
  </div>;
}